import { Pencil } from 'lucide-react'
import type { ReactNode } from 'react'

import type { ProductSummary } from '@aeris/contracts'
import { formatQuantity, parseQuantity, ZERO_QUANTITY } from '@aeris/inventory'
import {
  MONEY_SCALE,
  UNIT_PRICE_SCALE,
  compare,
  formatMoney,
  formatPercent,
  fromString,
  isNegative,
  isZero,
  parseHumanAmount,
  round,
} from '@aeris/money'
import { marginFromPrice, profitOf } from '@aeris/pricing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { CONDITION_LABEL } from '../lib/product-columns.tsx'

import { ProductIdentity } from './product-identity.tsx'

/**
 * A ficha do produto — F151.
 *
 * *"Só de clicar em cima tem que abrir em detalhes, e o detalhes ter opção de
 * editar também."*
 *
 * Até aqui produto **não tinha ficha**: a única porta era o FORMULÁRIO DE
 * EDIÇÃO, um diálogo com seis abas. Quem só queria conferir um preço no balcão
 * entrava numa tela onde qualquer tecla muda o cadastro — exatamente o que o
 * detalhe de venda (F150) tirou do caminho, e que produto ainda tinha.
 *
 * ## Nenhuma consulta nova
 *
 * O produto chega por prop, da linha que a lista **já carregou**:
 * `ProductSummary` traz nome, marca, variantes com SKU e GTIN, condição,
 * garantia, situação, foto e — quando a lista os pediu — preço, saldo, custo e
 * localização. Abrir a ficha não custa nem uma requisição.
 *
 * O reverso disso: o que a lista não pediu, a ficha não mostra. Ela diz "não
 * carregado" em vez de inventar um traço que parece "não tem".
 *
 * ## Por que não reaproveita o `ProductStockPanel`
 *
 * Aquele painel tem entrada, saída e ajuste dentro. O ponto desta ficha é ser
 * a porta onde nada muda sem querer: o saldo aparece como número, e mexer nele
 * continua sendo em Estoque ou no formulário.
 *
 * ## A ficha refeita
 *
 * A primeira versão era uma lista de dez linhas do mesmo peso, com o rótulo
 * numa ponta e o valor na outra: quem chegava com uma pergunta ("quanto custa?
 * tem quantos?") lia a lista inteira para achar duas linhas. Agora os números
 * que se consulta no balcão — preço, saldo, custo e margem — vêm em cartões
 * no topo, e o que só identifica a peça (código, barras, unidade) fica embaixo,
 * com um botão de copiar: o código vai para a busca do PDV e para a conversa
 * com o cliente.
 *
 * Continua sem consulta nova: tudo sai do `ProductSummary` que a lista trouxe.
 * Custo e margem só existem para quem tem `catalog.write`; para os outros o
 * servidor não manda o custo, e os dois cartões não aparecem — em vez de um
 * traço que pareceria "sem custo".
 */

const brl = (wire: string | null): string => {
  if (wire === null) return '—'
  const amount = parseHumanAmount(wire.replace('.', ','), MONEY_SCALE)
  return amount === null ? wire : formatMoney(amount)
}

const qty = (wire: string): string =>
  formatQuantity(parseQuantity(wire) ?? ZERO_QUANTITY)

/** Rótulo em cima, valor embaixo: o olho não atravessa a janela para casar os dois. */
const Line = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-(length:--text-xs) text-(--text-muted)">{label}</dt>
    <dd className="text-(length:--text-sm) text-(--text-primary)">
      {children}
    </dd>
  </div>
)

/** "não carregado" em texto pequeno: um valor grande diria que a lista o trouxe. */
const NotLoaded = (): ReactNode => (
  <span className="text-(length:--text-sm) font-(--weight-normal) text-(--text-secondary)">
    não carregado
  </span>
)

/**
 * Como está o saldo, em uma etiqueta.
 *
 * Só para produto que conta estoque, e só quando há o que dizer: peça com saldo
 * normal não ganha etiqueta nenhuma — o número fala por si.
 */
const stockFlag = (
  onHand: string,
  minimum: string | null
): { tone: 'warning' | 'danger'; text: string } | null => {
  const balance = parseQuantity(onHand) ?? ZERO_QUANTITY
  if (isNegative(balance)) return { tone: 'danger', text: 'Saldo negativo' }
  // Vermelho (F214): "sem estoque" é o que a loja precisa ver de longe.
  if (isZero(balance)) return { tone: 'danger', text: 'Sem estoque' }
  const floor = minimum === null ? null : parseQuantity(minimum)
  if (floor !== null && compare(balance, floor) < 0) {
    return {
      tone: 'warning',
      text: `Abaixo do mínimo (${qty(minimum ?? '0')})`,
    }
  }
  return null
}

export const ProductDetailDialog = ({
  product,
  onClose,
  onEdit,
}: {
  product: ProductSummary
  onClose: () => void
  /** Só quem tem `catalog.write`. Ausente, o botão não existe. */
  onEdit?: (() => void) | undefined
}): ReactNode => {
  const defaultVariant =
    product.variants.find((variant) => variant.isDefault) ?? product.variants[0]

  /*
   * Margem e lucro por peça, pela MESMA conta da lista e do cadastro (F123):
   * markup sobre o custo. Custo nulo ou zero não dá margem — "sem custo
   * cadastrado" é a resposta, e não um 100% que ninguém calculou.
   */
  const costRequested = product.cost !== undefined
  const cost = product.cost?.averageCost ?? null
  const price = product.price?.basePrice ?? null
  const margin =
    cost === null || price === null
      ? null
      : (() => {
          const costValue = fromString(cost, UNIT_PRICE_SCALE)
          const priceValue = fromString(price, UNIT_PRICE_SCALE)
          const percent = marginFromPrice(costValue, priceValue)
          return percent === null
            ? null
            : {
                // Uma casa: a lista guarda quatro, e "57,8947%" num cartão é
                // ruído — quem confere a margem no balcão quer "57,9%".
                percent: formatPercent(round(percent, 1, 'half-up')),
                profit: formatMoney(profitOf(costValue, priceValue)),
              }
        })()

  const flag =
    product.tracksStock && product.stock != null
      ? stockFlag(product.stock.onHand, product.minimumStock)
      : null

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={product.name}
        description={
          onEdit === undefined
            ? 'Só para conferir.'
            : 'Só para conferir — para alterar, use Editar.'
        }
        // Os mesmos códigos do Editar, com copiar (F248).
        meta={<ProductIdentity product={product} />}
        className="max-w-2xl"
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-4">
            {product.primaryImage === null ? null : (
              <img
                src={product.primaryImage.thumbUrl}
                alt={product.primaryImage.altText ?? product.name}
                className="h-24 w-24 shrink-0 rounded-(--radius-md) border border-(--border-subtle) object-cover"
              />
            )}
            <div className="flex min-w-0 flex-col gap-2">
              {/* Marca · categoria · condição: o que a peça É, numa linha. */}
              <p className="flex flex-wrap items-center gap-x-2 text-(length:--text-sm) text-(--text-secondary)">
                {[
                  product.brand,
                  product.categoryName,
                  CONDITION_LABEL[product.condition],
                ]
                  .filter((part): part is string => part != null)
                  .map((part, index) => (
                    <span key={part} className="flex items-center gap-2">
                      {index === 0 ? null : (
                        <span
                          aria-hidden="true"
                          className="text-(--text-muted)"
                        >
                          ·
                        </span>
                      )}
                      <span>{part}</span>
                    </span>
                  ))}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {product.status === 'archived' ? (
                  <Badge tone="neutral">Arquivado</Badge>
                ) : null}
                {product.awaitingStockSince !== null &&
                product.status === 'active' ? (
                  <Badge tone="warning">Aguardando estoque</Badge>
                ) : null}
                {product.tracksStock ? null : (
                  <Badge tone="neutral">Não conta estoque</Badge>
                )}
                {/* F214: no cabeçalho, e não só escondido no saldo. */}
                {flag === null ? null : (
                  <Badge tone={flag.tone}>{flag.text}</Badge>
                )}
              </div>
              {product.description === null ? null : (
                <p className="text-(length:--text-sm) text-(--text-secondary)">
                  {product.description}
                </p>
              )}
            </div>
          </div>

          {/*
            O que se consulta no balcão, à frente de tudo.

            Preço, saldo e custo só existem quando a LISTA os pediu. Dizer "não
            carregado" é diferente de "não tem" — e um traço aqui seria lido
            como a segunda coisa.
          */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))]">
            <StatTile
              label="Preço"
              value={
                product.price === undefined ? (
                  <NotLoaded />
                ) : product.price === null ? (
                  '—'
                ) : (
                  brl(product.price.basePrice)
                )
              }
              hint={
                product.price == null ? undefined : product.price.priceListName
              }
            />
            <StatTile
              label="Saldo nesta filial"
              value={
                !product.tracksStock ? (
                  <span className="text-(length:--text-sm) font-(--weight-normal) text-(--text-secondary)">
                    não controla
                  </span>
                ) : product.stock === undefined ? (
                  <NotLoaded />
                ) : product.stock === null ? (
                  '—'
                ) : (
                  qty(product.stock.onHand)
                )
              }
              hint={
                product.tracksStock && product.stock != null ? (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{`${qty(product.stock.available)} disponível`}</span>
                    {flag === null ? null : (
                      <Badge tone={flag.tone}>{flag.text}</Badge>
                    )}
                  </span>
                ) : undefined
              }
            />
            {!costRequested ? null : (
              <StatTile
                label="Custo médio"
                value={cost === null ? '—' : brl(cost)}
                hint={cost === null ? 'sem custo cadastrado' : undefined}
              />
            )}
            {!costRequested ? null : (
              <StatTile
                label="Margem"
                value={margin === null ? '—' : margin.percent}
                hint={
                  margin === null
                    ? cost === null
                      ? 'precisa do custo'
                      : 'precisa do preço'
                    : `lucro de ${margin.profit} por peça`
                }
              />
            )}
          </div>

          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-t border-(--border-subtle) pt-4 sm:grid-cols-2">
            {defaultVariant === undefined ? null : (
              <>
                {/* Código e código de barras moram no cabeçalho, com copiar (F248). */}
                <Line label="Unidade">{defaultVariant.unit}</Line>
              </>
            )}
            {product.warrantyMonths === null ? null : (
              <Line label="Garantia">
                {`${String(product.warrantyMonths)} ${product.warrantyMonths === 1 ? 'mês' : 'meses'}`}
              </Line>
            )}
            {product.location === undefined ? null : (
              <Line label="Onde fica">{product.location ?? '—'}</Line>
            )}
            {product.minimumStock === null ? null : (
              <Line label="Estoque mínimo">{qty(product.minimumStock)}</Line>
            )}
          </dl>

          {product.variants.length <= 1 ? null : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Cor</TableHeaderCell>
                    <TableHeaderCell>Código</TableHeaderCell>
                    <TableHeaderCell>Código de barras</TableHeaderCell>
                    <TableHeaderCell>Unidade</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product.variants.map((variant) => (
                    <TableRow key={variant.id}>
                      {/*
                        A cor primeiro (F214): a tabela mostrava só o código,
                        e "008155-2" não diz a ninguém que é a azul.
                      */}
                      <TableCell>
                        {variant.name === null ? (
                          <span className="text-(--text-muted)">Sem cor</span>
                        ) : (
                          <span className="font-(--weight-medium)">
                            {variant.name}
                          </span>
                        )}
                        {variant.isDefault ? (
                          <Badge tone="info" className="ml-2">
                            padrão
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-(--font-mono)">
                        {variant.sku}
                      </TableCell>
                      <TableCell className="font-(--font-mono)">
                        {variant.gtin ?? '—'}
                      </TableCell>
                      <TableCell>{variant.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {onEdit === undefined ? null : (
            <Button
              variant="primary"
              leadingIcon={<Pencil aria-hidden="true" className="h-4 w-4" />}
              onClick={onEdit}
            >
              Editar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
